import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CodeInput } from '~/components/CodeInput';
import { DEFAULT_CODE_LENGTH, requestLink, verifyCode } from '~/lib/auth';
import { loginHolds, secondsLeft, waitMessageOver } from '~/lib/signIn';
import { mountTurnstile, STAFF_LOGIN_ACTION, turnstileSiteKey, type TurnstileHandle } from '~/lib/turnstile';

/**
 * Admin sign-in (codes ported from the Hype Forge Base, 2026-10-21). Step 1
 * asks for the email; the API sends an 8-digit code and a link. Step 2 takes
 * the code, which signs in THIS browser (the link signs in whichever browser
 * the mail app opens; /auth/callback handles it). When the API publishes a
 * Turnstile site key (GET /api/public/config) one invisible widget runs on
 * both steps: its token goes with the request (action staff_login) and with
 * every code check (action staff_verify, 2026-10-22, from the Hype Forge
 * Base); the widget switches action with the step and is reset after every
 * attempt (lib/signIn.ts).
 *
 * Too many sign-in attempts (HTTP 429 with Retry-After; docs/sign-in.md): the
 * limited step shows "Too many sign-in attempts. Try again in 12 minutes.",
 * its button reads "Try again in 12 minutes" until the wait is over, and
 * "send a new code" waits it out too (2026-10-22, from the Hype Forge Base).
 *
 * Copy rule: no em or en dashes in visible text.
 */

type Phase = 'request' | 'sent';

const RESEND_SECONDS = 60;

/**
 * A wait the API asked for (HTTP 429 with Retry-After): the seconds left,
 * counting down, and the function that starts one. `left` is right on the
 * render that starts the wait, so nothing in between reads it as over.
 */
function useWait(): [number, (seconds: number) => void] {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const left = secondsLeft(until, now);
  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(id);
  }, [left, now]);
  const start = useCallback((seconds: number) => {
    const t = Date.now();
    setNow(t);
    setUntil(t + seconds * 1000);
  }, []);
  return [left, start];
}

export function LoginPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  // Honeypot: hidden from people, filled by form-stuffing bots. The API answers a
  // filled one with the same { ok } it gives everyone and sends nothing.
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A code is being asked for: a code check waits it out (the check would switch the widget under the request's token
  // wait and send it without one, or check the old code against the new one and spend one of its tries).
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [code, setCode] = useState('');
  // How many digits the emailed code has (the API says so in its answer).
  const [codeLength, setCodeLength] = useState(DEFAULT_CODE_LENGTH);
  const [checking, setChecking] = useState(false);
  // The last digit auto-submits; Enter right after must not send it twice.
  const checkingRef = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);
  // Bot protection on both steps: only when the API publishes a Turnstile site key.
  const turnstileEl = useRef<HTMLDivElement>(null);
  const turnstile = useRef<TurnstileHandle | null>(null);
  // Too many sign-in attempts: the API says how long to wait. Asking for a
  // code (Send, and "send a new code") and checking one are limited
  // separately; each button waits out its own.
  const [requestWait, waitForRequest] = useWait();
  const [verifyWait, waitForVerify] = useWait();
  // The error on screen is a wait message: it goes once both waits are over.
  const [errorIsWait, setErrorIsWait] = useState(false);
  useEffect(() => {
    if (waitMessageOver(errorIsWait, requestWait, verifyWait)) {
      setError(null);
      setErrorIsWait(false);
    }
  }, [errorIsWait, requestWait, verifyWait]);

  useEffect(() => {
    let cancelled = false;
    void turnstileSiteKey().then(async (key) => {
      if (!key || cancelled || !turnstileEl.current) return;
      const handle = await mountTurnstile(turnstileEl.current, key, STAFF_LOGIN_ACTION);
      if (cancelled) handle?.destroy();
      else turnstile.current = handle;
    });
    return () => {
      cancelled = true;
      turnstile.current?.destroy();
      turnstile.current = null;
    };
  }, []);

  // 60-second resend cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  async function sendLink(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    // Not while a code is being checked: a new code would retire the one in the check (and switch the widget under it).
    if (requestWait > 0 || checkingRef.current) return;
    setError(null);
    setErrorIsWait(false);
    submittingRef.current = true;
    setSubmitting(true);
    // The widget's token goes with the request; then it is switched to the code step, or reset for another try.
    const { error: err, codeLength: digits, retryAfter } = await requestLink(email.trim(), website, turnstile.current);
    submittingRef.current = false;
    setSubmitting(false);
    if (retryAfter) {
      // "Too many sign-in attempts. Try again in 12 minutes." Send and "send a new code" wait it out.
      waitForRequest(retryAfter);
      setResendCooldown((s) => Math.max(s, retryAfter));
      setError(err);
      setErrorIsWait(true);
      return;
    }
    if (err) {
      setError(err);
      return;
    }
    setCodeLength(digits);
    setPhase('sent');
    setCode('');
    setResendCooldown(RESEND_SECONDS);
  }

  // The code signs in THIS browser. Same session path as the link's callback page.
  async function submitCode(value: string) {
    // Not while a new code is being asked for either (the other way round from sendLink's guard).
    if (checkingRef.current || submittingRef.current || value.length !== codeLength || verifyWait > 0) return;
    checkingRef.current = true;
    setChecking(true);
    setError(null);
    setErrorIsWait(false);
    // With Turnstile on, a token for the code step goes with the code; the widget is reset after every attempt.
    const { error: err, retryAfter, turnstileFailed } = await verifyCode(email.trim(), value, turnstile.current);
    checkingRef.current = false;
    setChecking(false);
    if (retryAfter) {
      waitForVerify(retryAfter);
      setError(err);
      setErrorIsWait(true);
      return;
    }
    if (err) {
      // A failed Turnstile check spent no try: the typed code stays, ready to send again (and the words say press Sign
      // in, not reload, since a reload would drop this step; lib/signIn.ts, CODE_STEP_TURNSTILE_RETRY).
      setError(err);
      if (!turnstileFailed) setCode('');
      codeRef.current?.focus();
      return;
    }
    navigate('/', { replace: true });
  }

  // Which buttons a wait holds, and their words (lib/signIn.ts, tested in apps/admin/test/sign-in-wait.test.mjs).
  const holds = loginHolds({
    requestWait,
    verifyWait,
    resendCooldown,
    submitting,
    checking,
    hasEmail: email.trim() !== '',
    codeComplete: code.length === codeLength,
  });

  // Not while a code is being checked or a new one asked for: the answer would land on the email step (a check's token
  // wait would even end early once the widget switched, and the check go out without one).
  const stepBusy = checking || submitting;

  function back() {
    if (checkingRef.current || submitting) return;
    setPhase('request');
    if (!errorIsWait) setError(null);
    setCode('');
    turnstile.current?.use(STAFF_LOGIN_ACTION);
  }

  return (
    <div className="admin-login">
      <div className="admin-login__card">
        <h1 className="admin-login__title">Valley Vitality Admin</h1>

        {phase === 'request' && (
          <>
            <p className="admin-login__sub">
              Enter your email and we'll send you a one-time sign-in code.
            </p>
            <form onSubmit={sendLink}>
              <div className="admin-login__field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@growwithhype.com"
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              {/* Honeypot (see above): off screen, skipped by the tab order and screen readers. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
              />
              <button type="submit" className="admin-login__submit" disabled={holds.send.disabled}>
                {holds.send.label}
              </button>
              {error && (
                <div id="login-error" className="admin-login__error" role="alert">
                  {error}
                </div>
              )}
            </form>
          </>
        )}

        {phase === 'sent' && (
          <>
            <h2 className="admin-login__heading">Check your email</h2>
            <p className="admin-login__sub">
              If <strong>{email.trim()}</strong> has access, {codeLength === 8 ? 'an' : 'a'} {codeLength}-digit sign-in code
              is on its way. Enter it here to sign in on this device. It works for 15 minutes.
            </p>

            <form
              className="admin-login__codeform"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCode(code);
              }}
            >
              <div className="admin-login__field">
                <label htmlFor="code">Sign-in code</label>
                <CodeInput
                  id="code"
                  ref={codeRef}
                  value={code}
                  onChange={setCode}
                  onComplete={(v) => void submitCode(v)}
                  length={codeLength}
                  busy={checking || submitting}
                  autoFocus
                  describedBy={error ? 'code-error' : undefined}
                />
              </div>
              <button type="submit" className="admin-login__submit" disabled={holds.signIn.disabled}>
                {holds.signIn.label}
              </button>
              {error && (
                <div id="code-error" className="admin-login__error" role="alert">
                  {error}
                </div>
              )}
            </form>

            <div className="admin-login__tip">
              <strong>Didn't get it?</strong> Check your spam folder. First-time emails sometimes land there.{' '}
              {holds.resend.offered ? (
                <>
                  Then{' '}
                  <button type="button" className="admin-login__linkbtn" onClick={() => void sendLink()} disabled={submitting || checking}>
                    send a new code
                  </button>
                  . Only the newest code works.
                </>
              ) : (
                <span className="admin-login__muted">You can ask for another in {holds.resend.inText}.</span>
              )}
            </div>

            <p className="admin-login__help">
              The email also has a sign-in link. It signs in whichever browser your email app opens, so on this device the
              code is the surer way.
            </p>

            <p className="admin-login__help">
              <button type="button" className="admin-login__linkbtn admin-login__linkbtn--muted" onClick={back} disabled={stepBusy}>
                ← Use a different email
              </button>
            </p>
          </>
        )}
        {/* The Turnstile widget (invisible unless Cloudflare wants a click); kept across both steps, its action following the step ("send a new code" switches it back for the request). */}
        <div ref={turnstileEl} className="admin-login__turnstile" />
      </div>
    </div>
  );
}
